// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Pencil, Search, Trash2, RotateCw, Copy, Lock, Unlock } from 'lucide-react';
import { useCompanyContext } from '../context/CompanyContext';
import AddEditAnalysisPanel from './AddEditAnalysisPanel';
import DeleteAnalysisPanel from './DeleteAnalysisPanel';
import { updateAnalysis } from '../../../services/AnalysisApiClient';
import { useClientWorkspaceCompanyCard } from '../hooks/useClientWorkspaceCompanyCard';
import { isCompensationStepComplete, isBaseAmountStepComplete } from '../../../utils/compensationStep';
import { hasParachuteProgress } from '../components/WorkflowStepsPanel';
import TableRowKebabMenu from '../../shared/TableRowKebabMenu';
import { WorkspaceLoadingCard } from '../../shared/WorkspaceLoadingCard';
import { clientDataPanelClass, clientEmptyStateCardClass } from '../../../utils/clientChromeClasses';
import { ClientPageSummaryBar } from '../components/ClientPageSummaryBar';
import { isAnalysisLocked } from '../../../constants/analysisStatus';
import { pushToast } from '../../../utils/toast/toastStore';

export default function Analyses() {
  const {
    analyses,
    analysesListReady,
    setCurrentAnalysisId,
    createAnalysis,
    copyAnalysis,
    updateAnalysisName,
    deleteAnalysis,
    refreshAnalysesList,
    archivedAnalyses,
  } = useCompanyContext();

  /** Workspace company link (lawFirmId/companyId) for new-analysis POST. */
  const { workspaceCompany } = useClientWorkspaceCompanyCard(archivedAnalyses);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState('add');
  const [panelInitialName, setPanelInitialName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingInitiallyFinal, setEditingInitiallyFinal] = useState(false);
  const [copySourceId, setCopySourceId] = useState(null);
  const [copySourceLabel, setCopySourceLabel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletePanelOpen, setDeletePanelOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [actionsMenuForId, setActionsMenuForId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredAnalyses = searchLower
    ? analyses.filter((a) => {
        const name = (a.name || '').toLowerCase();
        const company = (a.data?.companyName || '').toLowerCase();
        const closing = (a.data?.closingDate || '').toLowerCase();
        return name.includes(searchLower) || company.includes(searchLower) || closing.includes(searchLower);
      })
    : analyses;

  const totalPages = Math.max(1, Math.ceil(filteredAnalyses.length / pageSize));
  const paginatedAnalyses = filteredAnalyses.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, analyses.length]);

  /** POST /analyses requires lawFirmId + companyId (server also fills for client users from Users when omitted). */
  const companyLinkForNewAnalysis = useMemo(() => {
    if (workspaceCompany?.lawFirmId && workspaceCompany?.companyId) {
      return { lawFirmId: workspaceCompany.lawFirmId, companyId: workspaceCompany.companyId };
    }
    const linked = analyses.find((a) => a.lawFirmId && a.companyId);
    if (linked?.lawFirmId && linked?.companyId) {
      return { lawFirmId: linked.lawFirmId, companyId: linked.companyId };
    }
    return undefined;
  }, [workspaceCompany, analyses]);

  const openAddPanel = () => {
    setPanelMode('add');
    setPanelInitialName('');
    setEditingId(null);
    setEditingInitiallyFinal(false);
    setCopySourceId(null);
    setCopySourceLabel('');
    setPanelOpen(true);
  };

  const suggestedCopyName = (sourceName) => {
    const base = (sourceName || 'Untitled analysis').trim();
    const suffix = ' (copy)';
    const combined = base + suffix;
    if (combined.length <= 200) return combined;
    return base.slice(0, Math.max(0, 200 - suffix.length)) + suffix;
  };

  const openCopyPanel = (a) => {
    setPanelMode('copy');
    setPanelInitialName(suggestedCopyName(a.name));
    setCopySourceId(a.id);
    setCopySourceLabel(a.name || 'Untitled analysis');
    setEditingId(null);
    setEditingInitiallyFinal(false);
    setPanelOpen(true);
  };

  const openEditPanel = (a) => {
    setPanelMode('edit');
    setPanelInitialName(a.name || 'Untitled analysis');
    setEditingId(a.id);
    setEditingInitiallyFinal(isAnalysisLocked(a.status));
    setCopySourceId(null);
    setCopySourceLabel('');
    setPanelOpen(true);
  };

  const handlePanelSave = (name, copyStepOptionsOrEdit) => {
    if (panelMode === 'add') {
      createAnalysis(name, companyLinkForNewAnalysis);
      return;
    }
    if (panelMode === 'copy' && copySourceId) {
      copyAnalysis(copySourceId, name, copyStepOptionsOrEdit);
      return;
    }
    const markFinal =
      copyStepOptionsOrEdit &&
      typeof copyStepOptionsOrEdit === 'object' &&
      'markFinal' in copyStepOptionsOrEdit &&
      copyStepOptionsOrEdit.markFinal;
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    if (markFinal) {
      return updateAnalysis(editingId, { name: trimmed, status: 'final' })
        .then(() => refreshAnalysesList())
        .catch((e) => {
          console.error(e);
          const msg = e instanceof Error && e.message.trim() ? e.message.trim() : 'Could not save analysis.';
          pushToast(msg, 'error');
          throw e;
        });
    }
    if (editingInitiallyFinal) {
      return updateAnalysis(editingId, { name: trimmed, status: 'editable' })
        .then(() => refreshAnalysesList())
        .catch((e) => {
          console.error(e);
          const msg = e instanceof Error && e.message.trim() ? e.message.trim() : 'Could not save analysis.';
          pushToast(msg, 'error');
          throw e;
        });
    }
    updateAnalysisName(editingId, trimmed);
  };

  const handlePanelClose = () => {
    setPanelOpen(false);
    setEditingId(null);
    setEditingInitiallyFinal(false);
    setCopySourceId(null);
    setCopySourceLabel('');
  };

  const openDeletePanel = (a) => {
    if (analyses.length <= 1) return;
    setAnalysisToDelete(a);
    setDeletePanelOpen(true);
  };

  const handleDeleteConfirm = (reason) => {
    if (analysisToDelete) {
      deleteAnalysis(analysisToDelete.id, reason);
      setAnalysisToDelete(null);
      setDeletePanelOpen(false);
    }
  };

  const handleDeletePanelClose = () => {
    setDeletePanelOpen(false);
    setAnalysisToDelete(null);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refreshAnalysesList().finally(() => setIsRefreshing(false));
  };

  /** Lock/unlock from row menu (same API as Edit analysis panel). */
  const persistAnalysisLockState = (a, status) => {
    const name = (a.name || '').trim() || 'Untitled analysis';
    return updateAnalysis(a.id, { name, status })
      .then(() => refreshAnalysesList())
      .catch((e) => {
        console.error(e);
        const msg = e instanceof Error && e.message.trim() ? e.message.trim() : 'Could not update analysis.';
        pushToast(msg, 'error');
      });
  };

  if (!analysesListReady) {
    return (
      <WorkspaceLoadingCard
        title="Loading analyses…"
        description="Fetching your workspace from the server."
        ariaLabel="Loading analyses"
        className="relative"
      />
    );
  }

  return (
    <div className="space-y-3 relative">
      <ClientPageSummaryBar
        intro={
          analyses.length > 0 ? (
            <p>
              Open an analysis to continue the workflow, or create a new one. Search by name, company, or closing date;
              use the row menu for duplicate, archive, and final lock.
            </p>
          ) : (
            <p>
              Create your first 280G analysis to get started. Company closing date and deal context appear here once
              linked.
            </p>
          )
        }
      />

      {analyses.length === 0 ? (
        <div className={clientEmptyStateCardClass}>
          <div
            className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4"
            title="280G analyses"
          >
            <FileText size={24} className="text-gray-400" aria-hidden />
          </div>
          <p className="text-gray-600 font-medium">No analyses yet</p>
          <p className="text-sm text-gray-500 mt-1 mb-6">Create your first 280G analysis to get started.</p>
          <button
            type="button"
            onClick={openAddPanel}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
            title="Create a new analysis"
          >
            <Plus size={18} aria-hidden />
            New Analysis
          </button>
        </div>
      ) : (
        <div className={clientDataPanelClass}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-3 border-b border-gray-200 bg-gray-50/80">
            <div
              className="relative w-full sm:flex-1 sm:min-w-0 sm:max-w-md"
              title="Search by analysis name, company, or closing date"
            >
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by analysis name, company, or closing date…"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                aria-label="Search analyses"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end sm:justify-start shrink-0">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center justify-center h-[42px] w-[42px] rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Refresh analyses and company summary"
                aria-label="Refresh analyses and company summary"
              >
                <RotateCw size={18} className={isRefreshing ? 'animate-spin' : ''} aria-hidden />
              </button>
              <button
                type="button"
                onClick={openAddPanel}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg btn-primary text-sm font-medium transition-colors shrink-0 focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                title="Create a new analysis"
              >
                <Plus size={18} aria-hidden />
                New Analysis
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Analysis Name
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Individuals
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deal Price
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AFR
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAnalyses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500">
                      No analyses match your search.
                    </td>
                  </tr>
                ) : (
                  paginatedAnalyses.map((a) => {
                    const wp = a.workflowProgress;
                    const individualsCount = wp?.individualsCount ?? a.data?.individuals?.length ?? 0;
                    const dp = a.data?.dealPrice;
                    const dpNum =
                      dp == null ? null : typeof dp === 'number' ? dp : parseFloat(String(dp).replace(/[^0-9.-]/g, ''));
                    const dealPriceFormatted =
                      dpNum != null && !isNaN(dpNum)
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(dpNum)
                        : '—';
                    const afrRaw = String(a.data?.afr ?? '').trim();
                    const afrDisplay = !afrRaw ? '—' : afrRaw.includes('%') ? afrRaw : `${afrRaw}%`;
                    const locked = isAnalysisLocked(a.status);
                    let completionPct;
                    let status;
                    let statusClass;
                    if (wp) {
                      completionPct = wp.percent;
                      status =
                        wp.status === 'complete'
                          ? 'Complete'
                          : wp.status === 'not_started'
                            ? 'Not started'
                            : 'In progress';
                      statusClass =
                        status === 'Complete'
                          ? 'bg-emerald-100/80 backdrop-blur-sm border border-emerald-200/60 text-emerald-800'
                          : status === 'In progress'
                            ? 'bg-amber-100/80 backdrop-blur-sm border border-amber-200/60 text-amber-800'
                            : 'bg-gray-100/80 backdrop-blur-sm border border-gray-200/60 text-gray-600';
                    } else {
                      const compensationStepDone = isCompensationStepComplete(a.data || {});
                      const baseAmountStepDone = isBaseAmountStepComplete(a.data || {});
                      const stepsDone = [
                        !!a.data?.companyName,
                        individualsCount > 0,
                        compensationStepDone,
                        baseAmountStepDone,
                        hasParachuteProgress(a.data || {}),
                      ].filter(Boolean).length;
                      completionPct = Math.round((stepsDone / 5) * 100);
                      status = stepsDone === 0 ? 'Not started' : stepsDone >= 5 ? 'Complete' : 'In progress';
                      statusClass =
                        status === 'Complete'
                          ? 'bg-emerald-100/80 backdrop-blur-sm border border-emerald-200/60 text-emerald-800'
                          : status === 'In progress'
                            ? 'bg-amber-100/80 backdrop-blur-sm border border-amber-200/60 text-amber-800'
                            : 'bg-gray-100/80 backdrop-blur-sm border border-gray-200/60 text-gray-600';
                    }
                    return (
                      <tr key={a.id} className="group">
                        <td className="px-5 py-4">
                          <div className="space-y-0.5">
                            <div className="inline-flex items-center gap-2">
                              <Link
                                to={`/client/${a.id}`}
                                onClick={() => setCurrentAnalysisId(a.id)}
                                className="font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                              >
                                {a.name || 'Untitled analysis'}
                              </Link>
                              <span
                                className={`inline-flex items-center ${locked ? 'text-red-600' : 'text-slate-500'}`}
                                title={
                                  locked
                                    ? 'Final: workflow edits and new runs are disabled.'
                                    : 'Editable: analysis is not final.'
                                }
                                aria-label={locked ? 'Final analysis' : 'Editable analysis'}
                              >
                                {locked ? (
                                  <Lock size={15} className="shrink-0" strokeWidth={2.25} aria-hidden />
                                ) : (
                                  <Unlock size={15} className="shrink-0" strokeWidth={2} aria-hidden />
                                )}
                              </span>
                            </div>
                            {(a.createdBy || a.createdOn) && (
                              <div className="text-xs text-gray-500">
                                {a.createdBy ? `First run by ${a.createdBy}` : null}
                                {a.createdBy && a.createdOn ? ' • ' : null}
                                {a.createdOn ? `Created on ${a.createdOn}` : null}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">{individualsCount}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{dealPriceFormatted}</td>
                        <td className="px-5 py-4 text-sm text-gray-600 tabular-nums">{afrDisplay}</td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium shadow-sm ${statusClass}`}
                            title={wp ? `${wp.stepsCompleted} of ${wp.totalSteps} workflow steps complete` : undefined}
                          >
                            <span className="font-medium tabular-nums">{completionPct}%</span>
                            <span className="opacity-90">{status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right sm:px-5 sm:py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openEditPanel(a)}
                              className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 focus-visible:ring-offset-1"
                              title={
                                locked ? 'Edit name (final — unlock in panel to edit freely)' : 'Edit analysis name'
                              }
                              aria-label="Edit analysis name"
                            >
                              <Pencil size={18} strokeWidth={2} aria-hidden />
                            </button>
                            <TableRowKebabMenu
                              ariaLabel="Analysis actions"
                              isOpen={actionsMenuForId === a.id}
                              onOpenChange={(open) => setActionsMenuForId(open ? a.id : null)}
                              entries={[
                                {
                                  type: 'item',
                                  id: 'edit',
                                  label: 'Edit',
                                  description: locked ? 'Final — unlock to edit freely' : undefined,
                                  icon: <Pencil size={16} strokeWidth={2} aria-hidden />,
                                  onClick: () => openEditPanel(a),
                                  tone: 'default',
                                },
                                {
                                  type: 'item',
                                  id: 'duplicate',
                                  label: 'Duplicate',
                                  icon: <Copy size={16} strokeWidth={2} aria-hidden />,
                                  onClick: () => openCopyPanel(a),
                                  tone: 'default',
                                },
                                locked
                                  ? {
                                      type: 'item',
                                      id: 'unlock',
                                      label: 'Unlock for editing',
                                      description: 'Allows workflow edits and new 280G runs',
                                      icon: <Unlock size={16} strokeWidth={2} aria-hidden />,
                                      onClick: () => persistAnalysisLockState(a, 'editable'),
                                      tone: 'default',
                                    }
                                  : {
                                      type: 'item',
                                      id: 'mark-final',
                                      label: 'Mark as final',
                                      description: 'Locks workflow edits and new runs',
                                      icon: <Lock size={16} strokeWidth={2} aria-hidden />,
                                      onClick: () => persistAnalysisLockState(a, 'final'),
                                      tone: 'neutral',
                                    },
                                { type: 'separator' },
                                {
                                  type: 'item',
                                  id: 'archive',
                                  label: 'Archive',
                                  icon: <Trash2 size={16} strokeWidth={2} aria-hidden />,
                                  onClick: () => openDeletePanel(a),
                                  disabled: analyses.length <= 1,
                                  title:
                                    analyses.length > 1
                                      ? 'Move this analysis to Archived analyses'
                                      : 'Create another analysis before archiving this one.',
                                  tone: 'danger',
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {filteredAnalyses.length > pageSize && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
              <div>
                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, filteredAnalyses.length)}</span> of{' '}
                <span className="font-medium">{filteredAnalyses.length}</span> analyses
              </div>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-l-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                  title="Previous page"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-xs">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-r-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                  title="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <AddEditAnalysisPanel
        isOpen={panelOpen}
        mode={panelMode}
        initialName={panelInitialName}
        copySourceLabel={copySourceLabel}
        editInitiallyFinal={editingInitiallyFinal}
        onClose={handlePanelClose}
        onSave={handlePanelSave}
      />
      <DeleteAnalysisPanel
        isOpen={deletePanelOpen}
        analysisName={analysisToDelete?.name}
        onClose={handleDeletePanelClose}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
